/**
 * Word-level diff helpers for inline AI rewrite review (accept/reject per hunk).
 */

export type DiffOp = { type: 'equal' | 'remove' | 'insert'; text: string };

export type DiffHunk =
  | { kind: 'equal'; text: string }
  | {
      kind: 'change';
      id: string;
      removed: string;
      inserted: string;
      decision: 'pending' | 'accepted' | 'rejected';
    };

/** Split keeping whitespace as separate tokens so layout stays readable. */
export function tokenizeWords(text: string): string[] {
  if (!text) return [];
  return text.match(/(\s+|[^\s]+)/g) ?? [text];
}

function lcsTable(a: string[], b: string[]): number[][] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp;
}

export function diffTokens(before: string, after: string): DiffOp[] {
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return b.length ? [{ type: 'insert', text: after }] : [];
  if (b.length === 0) return a.length ? [{ type: 'remove', text: before }] : [];

  // Guard huge texts — fall back to single replace hunk
  if (a.length * b.length > 250_000) {
    if (before === after) return [{ type: 'equal', text: before }];
    return [
      { type: 'remove', text: before },
      { type: 'insert', text: after },
    ];
  }

  const dp = lcsTable(a, b);
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      ops.push({ type: 'equal', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: 'remove', text: a[i] });
      i++;
    } else {
      ops.push({ type: 'insert', text: b[j] });
      j++;
    }
  }
  while (i < a.length) {
    ops.push({ type: 'remove', text: a[i++] });
  }
  while (j < b.length) {
    ops.push({ type: 'insert', text: b[j++] });
  }

  // Merge consecutive same-type ops
  const merged: DiffOp[] = [];
  for (const op of ops) {
    const last = merged[merged.length - 1];
    if (last && last.type === op.type) {
      last.text += op.text;
    } else {
      merged.push({ ...op });
    }
  }
  return merged;
}

export function opsToHunks(ops: DiffOp[], idPrefix: string): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let i = 0;
  let changeIdx = 0;
  while (i < ops.length) {
    const op = ops[i];
    if (op.type === 'equal') {
      hunks.push({ kind: 'equal', text: op.text });
      i++;
      continue;
    }
    let removed = '';
    let inserted = '';
    while (i < ops.length && ops[i].type !== 'equal') {
      if (ops[i].type === 'remove') removed += ops[i].text;
      else inserted += ops[i].text;
      i++;
    }
    hunks.push({
      kind: 'change',
      id: `${idPrefix}-${changeIdx++}`,
      removed,
      inserted,
      decision: 'pending',
    });
  }
  return hunks;
}

export function buildHunksFromTexts(before: string, after: string, idPrefix: string): DiffHunk[] {
  if (before === after) return before ? [{ kind: 'equal', text: before }] : [];
  return opsToHunks(diffTokens(before, after), idPrefix);
}

export function resolveHunksText(hunks: DiffHunk[]): string {
  return hunks
    .map((h) => {
      if (h.kind === 'equal') return h.text;
      if (h.decision === 'accepted') return h.inserted;
      // pending + rejected → keep original
      return h.removed;
    })
    .join('');
}

export function countPendingHunks(hunks: DiffHunk[]): number {
  return hunks.filter((h) => h.kind === 'change' && h.decision === 'pending').length;
}

export function decideAllHunks(hunks: DiffHunk[], decision: 'accepted' | 'rejected'): DiffHunk[] {
  return hunks.map((h) =>
    h.kind === 'change' && h.decision === 'pending' ? { ...h, decision } : h
  );
}
